import { useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import get from "lodash/get";

import { AuthContext } from "../../Contexts/AuthContext";
import useFetchApiCounter from "../api-counter/useFetchApiCounter";
import useFetchAllFeatureAccessUserList from "../user/useFetchAllFeatureAccessUserList";
import useFetchAllSubs from "../../Stripe/hook/useFetchAllSubs";
import {
  resolveApiQuota,
  nextCounterAfterSpend,
  MAX_FREE_API_COUNT,
  MAX_PAID_API_COUNT,
} from "../../helper/ai/apiQuota";
import {
  AI_QUOTA_EXCEEDED_EVENT,
  AI_QUOTA_SPENT_EVENT,
  emitAiQuotaSpent,
} from "../../utils/aiQuotaEvents";

let nextInstanceId = 0;

// Free: 15 lifetime. Paid: 500 per billing period.
// Unlimited ONLY when REACT_APP_IS_ALLOW_ALL_FEATURE_BY_DEV=true (local QA).
// Comp-list emails unlock downloads, not free AI.
//
//   if (!quota.canSpend()) return;
//   const response = await aiCall();
//   if (!response) return;
//   await quota.spend(); // local badge only — server already charged
const useAiQuota = () => {
  const { currentUser } = useContext(AuthContext);
  const uid = currentUser?.uid;
  const email =
    String(get(currentUser, "email", "") || "")
      .trim()
      .toLowerCase() || null;

  const {
    isLoading: isCounterLoading,
    isGotAPICounterResponse,
    response: apiCountResponse,
    counterDoc,
    setCounterDoc,
  } = useFetchApiCounter();
  const { allSubscriptions, isLoading: isSubsLoading } = useFetchAllSubs();
  const { allAccessIndividualUsers } = useFetchAllFeatureAccessUserList();

  const [localDoc, setLocalDoc] = useState(null);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [isPaidLimitOpen, setIsPaidLimitOpen] = useState(false);

  const hasSeededRef = useRef(false);

  useEffect(() => {
    if (!isGotAPICounterResponse || hasSeededRef.current) return;
    setLocalDoc(counterDoc);
    hasSeededRef.current = true;
  }, [isGotAPICounterResponse, counterDoc]);

  useEffect(() => {
    hasSeededRef.current = false;
    setLocalDoc(null);
  }, [uid]);

  // Server 402 → stamp the counter as exhausted and open the right modal.
  useEffect(() => {
    const onExceeded = (event) => {
      const detail = event?.detail || {};
      const tier = detail.tier === "paid" ? "paid" : "free";
      const used = Number(detail.used);
      const max = Number(detail.max);
      // An auto (non-click) call hit the limit: sync the badge, open nothing.
      const silent = Boolean(detail.silent);

      if (tier === "paid") {
        setLocalDoc((prev) => ({
          totalCount: Number(prev?.totalCount) || Number(counterDoc?.totalCount) || 0,
          paidPeriodKey:
            prev?.paidPeriodKey || counterDoc?.paidPeriodKey || null,
          paidCount: Number.isFinite(used)
            ? used
            : Math.max(
                Number(prev?.paidCount) || 0,
                Number(counterDoc?.paidCount) || 0,
                max || MAX_PAID_API_COUNT
              ),
        }));
        if (!silent) {
          setIsPaidLimitOpen(true);
          setIsPaywallOpen(false);
        }
        return;
      }

      setLocalDoc((prev) => ({
        totalCount: Number.isFinite(used)
          ? used
          : Math.max(
              Number(prev?.totalCount) || 0,
              Number(counterDoc?.totalCount) || 0,
              max || MAX_FREE_API_COUNT
            ),
        paidPeriodKey: prev?.paidPeriodKey || counterDoc?.paidPeriodKey || null,
        paidCount: Number(prev?.paidCount) || Number(counterDoc?.paidCount) || 0,
      }));
      if (setCounterDoc) {
        setCounterDoc((prev) => ({
          ...(prev || {}),
          totalCount: Number.isFinite(used)
            ? used
            : Math.max(
                Number(prev?.totalCount) || 0,
                max || MAX_FREE_API_COUNT
              ),
        }));
      }
      if (!silent) {
        setIsPaywallOpen(true);
        setIsPaidLimitOpen(false);
      }
    };

    window.addEventListener(AI_QUOTA_EXCEEDED_EVENT, onExceeded);
    return () => window.removeEventListener(AI_QUOTA_EXCEEDED_EVENT, onExceeded);
  }, [counterDoc, setCounterDoc]);

  const effectiveDoc =
    localDoc || counterDoc || { totalCount: apiCountResponse || 0 };

  const quota = useMemo(
    () =>
      resolveApiQuota({
        allSubscriptions,
        email,
        allAccessUsers: allAccessIndividualUsers,
        counterDoc: effectiveDoc,
      }),
    [allSubscriptions, email, allAccessIndividualUsers, effectiveDoc]
  );

  // { silent: true } for calls the page makes on its own: still blocked when
  // exhausted, but no paywall pops up for an action the user did not take.
  const canSpend = useCallback((options) => {
    if (quota.isUnlimited) return true;
    if (!isGotAPICounterResponse) return true;

    if (quota.isExhausted) {
      if (options?.silent === true) return false;
      if (quota.isPaidLimitReached) setIsPaidLimitOpen(true);
      else setIsPaywallOpen(true);
      return false;
    }
    return true;
  }, [isGotAPICounterResponse, quota]);

  // Each component calling this hook holds its own copy of the counter, so a
  // spend is also broadcast: the floating AiQuotaWidget (and any other copy on
  // the page) moves with it. The id lets this copy ignore its own broadcast.
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) {
    nextInstanceId += 1;
    instanceIdRef.current = nextInstanceId;
  }

  // Move this copy's badge by `units`. Functional updates, so two spends in
  // one handler (a long posting's condense, then the tailor) stack instead of
  // both writing "used + 1" from the counter seen at click time.
  const applySpend = useCallback(
    (units = 1) => {
      if (quota.isUnlimited) return;

      const count = Math.max(1, Math.floor(Number(units) || 1));
      const advance = (doc) => {
        let next = doc;
        for (let i = 0; i < count; i += 1) next = nextCounterAfterSpend(next, quota);
        return next;
      };

      setLocalDoc((prev) => advance(prev || effectiveDoc));
      if (setCounterDoc) setCounterDoc((prev) => advance(prev || effectiveDoc));
    },
    [quota, effectiveDoc, setCounterDoc]
  );

  const spend = useCallback(
    (units = 1) => {
      if (quota.isUnlimited) return Promise.resolve(true);
      const count = Math.max(1, Math.floor(Number(units) || 1));
      applySpend(count);
      emitAiQuotaSpent({ units: count, sourceId: instanceIdRef.current });
      return Promise.resolve(true);
    },
    [quota.isUnlimited, applySpend]
  );

  // A spend made by another copy of this hook, or by a component without one
  // (emitAiQuotaSpent() from the posting helpers: no sourceId, applies here).
  const applySpendRef = useRef(applySpend);
  applySpendRef.current = applySpend;

  useEffect(() => {
    const onSpent = (event) => {
      const detail = event?.detail || {};
      if (detail.sourceId && detail.sourceId === instanceIdRef.current) return;
      applySpendRef.current(detail.units || 1);
    };
    window.addEventListener(AI_QUOTA_SPENT_EVENT, onSpent);
    return () => window.removeEventListener(AI_QUOTA_SPENT_EVENT, onSpent);
  }, []);

  return {
    ...quota,
    isLoading: isCounterLoading || isSubsLoading,
    isReady: isGotAPICounterResponse,
    isSpending: false,
    canSpend,
    spend,
    isPaywallOpen,
    openPaywall: () => setIsPaywallOpen(true),
    closePaywall: () => setIsPaywallOpen(false),
    isPaidLimitOpen,
    closePaidLimit: () => setIsPaidLimitOpen(false),
  };
};

export default useAiQuota;
